import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';
import type { Aporte } from '@/mocks/store';

function fmt(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

export default function AportesPage() {
  const { aportes, addAporte, updateAporte, deleteAporte, totalAportes } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Aporte | null>(null);
  const [form, setForm] = useState({ valor: '', data: new Date().toISOString().split('T')[0], observacao: '' });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  const aportesFiltrados = aportes.filter(a => {
    if (!a.data) return true;
    const [av, mv] = a.data.split('-');
    return av === anoStr && mv === mesPad;
  });
  const totalFiltrado = aportesFiltrados.reduce((acc, a) => acc + a.valor, 0);

  function openNew() { setEditingItem(null); setForm({ valor: '', data: new Date().toISOString().split('T')[0], observacao: '' }); setShowModal(true); }
  function openEdit(a: Aporte) { setEditingItem(a); setForm({ valor: String(a.valor), data: a.data, observacao: a.observacao }); setShowModal(true); }

  function handleSave() {
    if (!form.valor) return;
    if (editingItem) {
      updateAporte(editingItem.id, { valor: Number(form.valor), data: form.data, observacao: form.observacao });
    } else {
      addAporte({ valor: Number(form.valor), data: form.data, observacao: form.observacao });
    }
    setShowModal(false);
    setEditingItem(null);
    setForm({ valor: '', data: new Date().toISOString().split('T')[0], observacao: '' });
  }

  return (
    <AppLayout>
      <TopBar title="Aportes" subtitle="Capital próprio investido na loja" />
      <ActionBar>
        <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />
        <button onClick={openNew} className="btn-gold w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> Novo Aporte
        </button>
      </ActionBar>
      <div className="pt-[200px] md:pt-[200px] p-3 md:p-6 space-y-3 animate-fade-in-up">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Total Aportado</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-yellow-400">{fmt(totalFiltrado)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Nº de Aportes</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-dark-text">{aportesFiltrados.length}</p>
          </div>
        </div>

        <div className="bg-neon/5 border border-neon/20 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line text-neon text-sm"></i></div>
            <p className="text-xs text-dark-muted leading-relaxed">Aportes são o dinheiro do seu bolso investido na loja. Quanto menor em relação ao lucro, mais independente a loja está.</p>
          </div>
        </div>

        <div className="space-y-2">
          {aportesFiltrados.length === 0 ? (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 flex flex-col items-center text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card2 mb-2"><i className="ri-arrow-down-circle-line text-xl text-dark-muted"></i></div>
              <p className="text-sm text-dark-muted">Nenhum aporte neste mês</p>
            </div>
          ) : aportesFiltrados.map(a => (
            <div key={a.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-dark-text text-sm truncate">{a.observacao || 'Aporte de capital'}</p>
                  <p className="text-[10px] text-dark-muted mt-0.5">{new Date(a.data).toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <p className="text-base font-bold font-mono text-yellow-400">{fmt(a.valor)}</p>
                  <button onClick={() => openEdit(a)} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-neon cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => setConfirmDelete(a.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-dark-muted hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingItem ? 'Editar Aporte' : 'Novo Aporte'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Valor (R$)</label><input type="number" className="input-dark" placeholder="0,00" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} /></div>
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Data</label><input type="date" className="input-dark" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></div>
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Observação</label><input className="input-dark" placeholder="Ex: Capital inicial para estoque" value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editingItem ? 'Salvar' : 'Registrar Aporte'}</button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <h3 className="text-center text-base font-bold mb-2 text-dark-text">Excluir aporte?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteAporte(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
